// Grid-mutation history. One transaction = one trigger-press → release
// gesture; everything mutated between begin/end gets coalesced into a
// single diff that's pushed onto the undo stack.
//
// Recording happens transparently: VoxelGrid.set / VoxelGrid.delete
// call `recordChange` before mutating, and `recordChange` is a no-op
// unless a transaction is active. Undo/redo apply the diff in reverse/
// forward with recording suppressed so they don't loop.

import type { PaletteIndex, VoxelGrid } from "./voxel-grid";

// A transaction's grid edits as PARALLEL FLAT ARRAYS rather than an array of
// {ix,iy,iz,prev,next} objects. A Fill / large box writes millions of cells, and
// per-cell object allocation (and a string-keyed Map) dominated commit time;
// flat arrays + a numeric coalescing index cut it several-fold. prev/next are
// palette indices (0 = empty). Entries where prev === next are no-ops left in
// place (apply skips them) — cheaper than rebuilding the arrays to filter.
interface GridChanges {
  ix: number[];
  iy: number[];
  iz: number[];
  prev: number[];
  next: number[];
}

function emptyChanges(): GridChanges {
  return { ix: [], iy: [], iz: [], prev: [], next: [] };
}

export interface GridDiff {
  changes: GridChanges;
  // Optional whole-model snapshot, restored via the registered handler. Used for
  // canvas resizing: bounds changes + voxel pruning + the recenter shift make a
  // per-cell diff impractical, so undo/redo just put the before/after state back
  // wholesale. A transaction commits if it has changes OR a snapshot.
  snapshot?: { before: unknown; after: unknown };
  // Optional selection state before/after the transaction (a canonical string of
  // sorted cell keys, so they compare by value). Restored on undo/redo so that
  // selecting is itself an undoable step, interleaved with the grid edits — undo
  // a move and the cells stay selected; undo again and the selection lifts.
  selBefore?: string;
  selAfter?: string;
  // Optional reference-image transform before/after (its transform JSON; "" =
  // no image). The reference is saved with the model, so its edits belong on
  // the SAME timeline as the voxel edits — Ctrl+Z reverses the user's actual
  // last action whether it touched cells or the picture, instead of routing
  // by an invisible "editing the reference" flag.
  reference?: { before: string; after: string };
  // Optional palette before/after (full color table). A swatch recolor pairs a
  // palette change with a voxel re-index (existing voxels move to a frozen slot
  // holding their old color) — both must undo together, or undoing the re-index
  // would land voxels back on an index whose color has since changed.
  palette?: { before: number[]; after: number[] };
}

// Bounded so a runaway drag-paint doesn't grow the stack without limit.
// 200 is plenty for typical editing sessions.
const MAX_HISTORY = 200;

const undoStack: GridDiff[] = [];
const redoStack: GridDiff[] = [];

// Active transaction's flat edit log, appended by recordChange.
let active: GridChanges | null = null;
// Maps a packed cell key → that cell's index in `active`, so a second edit of
// the same cell coalesces (keep original prev, update next) instead of adding a
// row. Null in BULK mode: a Fill / box-commit writes each cell exactly once, so
// the coalescing map is pure overhead — skipping it is the bulk speedup.
let activeIndex: Map<number, number> | null = null;

// Pack a cell triple into one Number for the coalescing index. 16-bit biased
// per axis keeps the product under 2^53 for any coord in [-32768, 32767] (well
// beyond the volume cap).
const CELL_KEY_BIAS = 32768;
const CELL_KEY_RANGE = 65536;
function cellKeyNum(ix: number, iy: number, iz: number): number {
  return (
    ((ix + CELL_KEY_BIAS) * CELL_KEY_RANGE + (iy + CELL_KEY_BIAS)) *
      CELL_KEY_RANGE +
    (iz + CELL_KEY_BIAS)
  );
}
let activeSnapshot: { before: unknown; after: unknown } | null = null;
let activeSelBefore: string | null = null;
let activePalette: { before: number[]; after: number[] } | null = null;
let suppressed = false;

// Restores a snapshot recorded via recordSnapshot. main wires this to apply a
// captured model state (grid cells + canvas bounds + voxel scale).
type SnapshotApply = (state: unknown) => void;
let snapshotApply: SnapshotApply | null = null;
export function onSnapshotApply(fn: SnapshotApply | null): void {
  snapshotApply = fn;
}

// Attach a whole-model before/after snapshot to the active transaction.
export function recordSnapshot(before: unknown, after: unknown): void {
  if (suppressed || !active) return;
  activeSnapshot = { before, after };
}

// Restores a palette snapshot (the full color table). main wires this to
// applyPaletteSnapshot + refresh so undo/redo of a swatch recolor puts the
// colors back in lockstep with the voxel re-index.
type PaletteApply = (colors: number[]) => void;
let paletteApply: PaletteApply | null = null;
export function onPaletteApply(fn: PaletteApply | null): void {
  paletteApply = fn;
}

// Attach a palette before/after to the active transaction (see GridDiff.palette).
export function recordPaletteChange(before: number[], after: number[]): void {
  if (suppressed || !active) return;
  activePalette = { before, after };
}

// Capture/restore the current selection, wired by main. Lets the history treat
// selecting as an undoable action: every transaction records the selection
// before and after (as a canonical string), and undo/redo put it back.
type SelectionCapture = () => string;
type SelectionApply = (state: string) => void;
let selectionCapture: SelectionCapture | null = null;
let selectionApply: SelectionApply | null = null;
export function onSelectionHistory(
  capture: SelectionCapture,
  apply: SelectionApply,
): void {
  selectionCapture = capture;
  selectionApply = apply;
}

// Applies a reference-image transform JSON ("" = no image at that point, so
// there's nothing to apply). Wired by main.
type ReferenceApply = (json: string) => void;
let referenceApply: ReferenceApply | null = null;
export function onReferenceApply(fn: ReferenceApply | null): void {
  referenceApply = fn;
}

// Record a reference-image transform change as its own undo step on the model
// timeline. Counts as a model edit (the transform is persisted with the
// model), so it fires the commit listener and shows in historyDepth.
export function commitReferenceChange(before: string, after: string): void {
  if (before === after) return;
  const diff: GridDiff = { changes: emptyChanges(), reference: { before, after } };
  undoStack.push(diff);
  while (undoStack.length > MAX_HISTORY) undoStack.shift();
  redoStack.length = 0;
  commitListener?.(diff);
}

// Optional callback fired right after a transaction commits with a
// non-empty diff. main.ts wires this to the autosaver so persistence
// piggybacks on every undo-stack push.
type CommitListener = (diff: GridDiff) => void;
let commitListener: CommitListener | null = null;

export function onTransactionCommit(listener: CommitListener | null): void {
  commitListener = listener;
}

// Open a transaction. Pass bulk=true for whole-volume edits (Fill/Clear) that
// write each cell exactly once: it skips the per-cell coalescing index, the bulk
// speedup. Gestures that may re-touch a cell leave it false.
export function beginTransaction(bulk = false): void {
  active = emptyChanges();
  activeIndex = bulk ? null : new Map();
  activeSnapshot = null;
  activePalette = null;
  activeSelBefore = selectionCapture ? selectionCapture() : null;
}

// Drop coalescing for the rest of the OPEN transaction — used by the box-draw
// commit, whose one-shot fill (started during a normal gesture transaction)
// writes each cell once. Safe only when no further re-touches will occur.
export function disableCoalescing(): void {
  activeIndex = null;
}

// Close the in-progress transaction. If any non-no-op changes were
// recorded, push them as a single diff onto the undo stack and clear
// the redo stack. Returns the pushed diff (or null if empty / no
// transaction was open).
export function endTransaction(): GridDiff | null {
  if (!active) return null;
  const changes = active;
  const snapshot = activeSnapshot;
  const palette = activePalette;
  const selBefore = activeSelBefore;
  active = null;
  activeIndex = null;
  activeSnapshot = null;
  activePalette = null;
  activeSelBefore = null;
  // Any real (non-no-op) grid edit? Early-exits on the first, so a Fill is O(1)
  // here. Coalesced set+delete of the same cell leaves prev === next, so a
  // net-no-op gesture reports none and records no undo step.
  let hasRealChange = false;
  for (let i = 0; i < changes.prev.length; i++) {
    if (changes.prev[i] !== changes.next[i]) {
      hasRealChange = true;
      break;
    }
  }
  const selAfter = selectionCapture ? selectionCapture() : null;
  const selChanged =
    selBefore !== null && selAfter !== null && selBefore !== selAfter;
  if (!hasRealChange && !snapshot && !palette && !selChanged) return null;
  const diff: GridDiff = {
    changes: hasRealChange ? changes : emptyChanges(),
    snapshot: snapshot ?? undefined,
    palette: palette ?? undefined,
    selBefore: selBefore ?? undefined,
    selAfter: selAfter ?? undefined,
  };
  undoStack.push(diff);
  while (undoStack.length > MAX_HISTORY) undoStack.shift();
  redoStack.length = 0;
  // Only model changes (grid/bounds/palette) need an autosave; a selection-only
  // step doesn't alter the persisted model.
  if (hasRealChange || snapshot || palette) commitListener?.(diff);
  return diff;
}

// Record a standalone selection change (no grid edit) as its own undo step —
// used by the desktop marquee / click-select, which mutate the selection
// directly rather than inside a transaction. No-op when nothing changed.
export function commitSelectionChange(before: string, after: string): void {
  if (before === after) return;
  const diff: GridDiff = { changes: emptyChanges(), selBefore: before, selAfter: after };
  undoStack.push(diff);
  while (undoStack.length > MAX_HISTORY) undoStack.shift();
  redoStack.length = 0;
}

// Called by VoxelGrid.set/delete. Appends one row to the flat log. With the
// coalescing index on (normal gestures), a second edit of the same cell updates
// that row's `next` in place (keeping the original `prev`) instead of adding a
// row; in bulk mode every call appends.
export function recordChange(
  ix: number,
  iy: number,
  iz: number,
  prev: PaletteIndex,
  next: PaletteIndex,
): void {
  if (suppressed) return;
  if (!active) return;
  if (activeIndex) {
    const key = cellKeyNum(ix, iy, iz);
    const at = activeIndex.get(key);
    if (at !== undefined) {
      active.next[at] = next; // keep original prev; update final next
      return;
    }
    activeIndex.set(key, active.ix.length);
  }
  active.ix.push(ix);
  active.iy.push(iy);
  active.iz.push(iz);
  active.prev.push(prev);
  active.next.push(next);
}

export function undo(grid: VoxelGrid): boolean {
  const diff = undoStack.pop();
  if (!diff) return false;
  applyDiff(grid, diff, true);
  redoStack.push(diff);
  if (modelChanging(diff)) commitListener?.(diff);
  return true;
}

export function redo(grid: VoxelGrid): boolean {
  const diff = redoStack.pop();
  if (!diff) return false;
  applyDiff(grid, diff, false);
  undoStack.push(diff);
  if (modelChanging(diff)) commitListener?.(diff);
  return true;
}

// Does this entry alter the persisted model (vs a selection-only step)?
function modelChanging(d: GridDiff): boolean {
  return (
    d.changes.ix.length > 0 ||
    d.snapshot !== undefined ||
    d.reference !== undefined ||
    d.palette !== undefined
  );
}

// Drop all undo/redo state — used when loading a model replaces the grid, so
// undo can't reach into the previous model's edits.
export function clearHistory(): void {
  undoStack.length = 0;
  redoStack.length = 0;
}

// Number of committed MODEL edits behind the current position (selection-only
// steps are excluded — they don't change the persisted model, so they mustn't
// make it look dirty). Since any new edit clears the redo stack, this identifies
// the spot in the model's linear history, so callers can detect "we've undone
// back to the saved point" by remembering the depth at save time and comparing.
export function historyDepth(): number {
  let n = 0;
  for (const d of undoStack) if (modelChanging(d)) n++;
  return n;
}

// Run `fn` with recording disabled — used by undo/redo themselves and
// by the initial-state seeding so neither leaks into the history.
export function suppressRecording<T>(fn: () => T): T {
  const prev = suppressed;
  suppressed = true;
  try {
    return fn();
  } finally {
    suppressed = prev;
  }
}

function applyDiff(grid: VoxelGrid, diff: GridDiff, reverse: boolean): void {
  suppressRecording(() => {
    const ch = diff.changes;
    const n = ch.ix.length;
    for (let i = 0; i < n; i++) {
      if (ch.prev[i] === ch.next[i]) continue; // no-op row
      const target = reverse ? ch.prev[i] : ch.next[i];
      if (target === 0) grid.delete(ch.ix[i], ch.iy[i], ch.iz[i]);
      else grid.set(ch.ix[i], ch.iy[i], ch.iz[i], target);
    }
  });
  if (diff.snapshot && snapshotApply) {
    snapshotApply(reverse ? diff.snapshot.before : diff.snapshot.after);
  }
  if (diff.palette && paletteApply) {
    paletteApply(reverse ? diff.palette.before : diff.palette.after);
  }
  if (
    selectionApply &&
    (diff.selBefore !== undefined || diff.selAfter !== undefined)
  ) {
    selectionApply((reverse ? diff.selBefore : diff.selAfter) ?? "");
  }
  if (diff.reference && referenceApply) {
    referenceApply(reverse ? diff.reference.before : diff.reference.after);
  }
}
